import React from "react";
import { FeatherAlertCircle } from "@subframe/core";
import { Table, Badge } from "../../ui";
import { VendorPaymentCategoryDetails } from "../../services/api";

interface VendorPaymentViewContentProps {
  details: VendorPaymentCategoryDetails | null;
  isLoading: boolean;
}

const VendorPaymentViewContent: React.FC<VendorPaymentViewContentProps> = ({
  details,
  isLoading,
}) => {
  if (isLoading) {
    return (
      <div className="flex w-full items-center justify-center rounded-md border border-dashed border-neutral-border bg-neutral-50 px-6 py-10 text-body text-subtext-color">
        Loading vendor payment details...
      </div>
    );
  }

  if (!details) {
    return (
      <div className="flex w-full flex-col items-center gap-2 rounded-md border border-dashed border-neutral-border bg-neutral-50 px-6 py-10 text-center text-body text-subtext-color">
        <FeatherAlertCircle className="text-neutral-400" />
        <span>
          Vendor payment details are not available for this transaction.
        </span>
      </div>
    );
  }

  const currencySymbol =
    details.bills[0]?.currency_symbol ||
    details.currency_symbol ||
    details.currency_code ||
    "₹";
  const totalApplied = details.bills.reduce(
    (sum, bill) => sum + (bill.amount_applied || 0),
    0
  );
  const totalAdjustment = details.bills.reduce(
    (sum, bill) => sum + (bill.adjustment_amount || 0),
    0
  );

  const formatAmount = (amount: number): string =>
    amount.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const formatExchangeRate = (rate: number): string => {
    const truncated = Math.trunc(rate * 1_000_000) / 1_000_000;
    return truncated.toString();
  };

  const showExchangeRate =
    details.exchange_rate != null && details.exchange_rate > 0;
  const showBankCharges =
    details.bank_charges != null && details.bank_charges > 0;
  const showForexSummary = showExchangeRate || showBankCharges;

  return (
    <div className="flex w-full flex-col gap-4 rounded-md border border-solid border-neutral-border bg-neutral-50 px-6 py-6">
      <div className="flex w-full items-center justify-between gap-4">
        <div className="flex flex-col gap-1">
          <span className="text-heading-3 font-heading-3 text-default-font">
            Vendor Payment
          </span>
          <div className="flex flex-wrap items-center gap-2">
            {details.vendor_name && (
              <Badge className="border-success-700 bg-success-700 text-white [&>span]:text-white">
                Vendor: {details.vendor_name}
              </Badge>
            )}
          </div>
        </div>
      </div>

      {details.bills.length === 0 ? (
        <div className="flex w-full flex-col items-center gap-2 rounded border border-dashed border-neutral-border px-4 py-6 text-center text-body text-subtext-color">
          <FeatherAlertCircle className="text-neutral-400" />
          <span>No bills were linked to this payment.</span>
        </div>
      ) : (
        <Table
          header={
            <Table.HeaderRow>
              <Table.HeaderCell>Bill #</Table.HeaderCell>
              <Table.HeaderCell>Total</Table.HeaderCell>
              <Table.HeaderCell>Balance</Table.HeaderCell>
              <Table.HeaderCell>Amount Applied</Table.HeaderCell>
            </Table.HeaderRow>
          }
        >
          {details.bills.map((bill) => (
            <Table.Row key={bill.bill_id}>
              <Table.Cell>
                <span className="text-body-bold">{bill.bill_number}</span>
              </Table.Cell>
              <Table.Cell>
                {bill.total !== null && bill.total !== undefined ? (
                  <span className="text-body">
                    {bill.currency_symbol || currencySymbol}{" "}
                    {bill.total.toLocaleString("en-IN")}
                  </span>
                ) : (
                  <span className="text-caption text-subtext-color">—</span>
                )}
              </Table.Cell>
              <Table.Cell>
                {bill.balance !== null && bill.balance !== undefined ? (
                  <span className="text-body">
                    {bill.currency_symbol || currencySymbol}{" "}
                    {bill.balance.toLocaleString("en-IN")}
                  </span>
                ) : (
                  <span className="text-caption text-subtext-color">—</span>
                )}
              </Table.Cell>
              <Table.Cell>
                <div className="flex flex-col gap-0.5">
                  <span className="text-body-bold text-success-600">
                    {bill.currency_symbol || currencySymbol}{" "}
                    {bill.amount_applied.toLocaleString("en-IN")}
                  </span>
                  {(bill.adjustment_amount || 0) > 0 && (
                    <span className="text-caption text-subtext-color">
                      Adjustment: {bill.currency_symbol || currencySymbol}{" "}
                      {(bill.adjustment_amount || 0).toLocaleString("en-IN")}
                    </span>
                  )}
                </div>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table>
      )}

      <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-3">
        <div className="flex flex-col rounded-md border border-dashed border-neutral-border px-4 py-3">
          <span className="text-caption text-subtext-color">
            Amount Applied
          </span>
          <span className="text-heading-4 font-heading-4 text-default-font">
            {currencySymbol} {totalApplied.toLocaleString("en-IN")}
          </span>
        </div>
        <div className="flex flex-col rounded-md border border-dashed border-neutral-border px-4 py-3">
          <span className="text-caption text-subtext-color">Adjustment</span>
          <span className="text-heading-4 font-heading-4 text-default-font">
            {currencySymbol} {totalAdjustment.toLocaleString("en-IN")}
          </span>
        </div>
        <div className="flex flex-col rounded-md border border-dashed border-neutral-border px-4 py-3">
          <span className="text-caption text-subtext-color">
            Advance Payment
          </span>
          <span className="text-heading-4 font-heading-4 text-default-font">
            {currencySymbol}{" "}
            {formatAmount(details.advance_payment || 0)}
          </span>
        </div>
      </div>

      {showForexSummary && (
        <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2">
          {showExchangeRate && (
            <div className="flex flex-col rounded-md border border-dashed border-neutral-border px-4 py-3">
              <span className="text-caption text-subtext-color">
                Exchange Rate
              </span>
              <span className="text-heading-4 font-heading-4 text-default-font">
                {formatExchangeRate(details.exchange_rate!)}
              </span>
            </div>
          )}
          {showBankCharges && (
            <div className="flex flex-col rounded-md border border-dashed border-neutral-border px-4 py-3">
              <span className="text-caption text-subtext-color">
                Bank Charges
              </span>
              <span className="text-heading-4 font-heading-4 text-default-font">
                {currencySymbol} {formatAmount(details.bank_charges!)}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VendorPaymentViewContent;
